import { useQuery } from "@apollo/client/react";
import Link from "next/link";
import { use, useRef } from "react";
import { LuMessagesSquare } from "react-icons/lu";

import { DIALOG } from "@/components/app/dialogs/dialog-provider";
import { useLocalEntryDialog } from "@/components/app/dialogs/use-local-entry-dialog";
import type { EditorContextType } from "@/components/app/editor-v3/editor-context";
import { EditorContextProvider } from "@/components/app/editor-v3/editor-context";
import { CommentOverlayEditor } from "@/components/app/editor-v3/editors/comment-overlay-editor";
import { toPostRequestParams } from "@/components/app/editor-v3/transforms/to-post-request-params";
import { StopPropagationWrapper } from "@/components/shared/stop-propagation-wrapper";
import { useConfirm } from "@/components/ui/confirm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { PostContextProvider } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import { PostByPostHashDocument } from "@/graphql/codegen/graphql";
import { useSubmitPost } from "@/hooks/use-submit-post";
import { getPostData } from "@/utils/post-utils";
import { getUserName } from "@/utils/usernames";

import { NotificationPostDetails } from "./notification-post-details";

const NOTIFICATION_POST_SHOW_MORE_MAX_HEIGHT = 150;

export const NotificationPost = ({
  postHash,
  replyPostHash,
  notificationId,
  hideNotificationHeader,
}: {
  postHash: string;
  replyPostHash?: string;
  notificationId: string;
  hideNotificationHeader?: boolean;
}) => {
  const { data: postData, loading } = useQuery(PostByPostHashDocument, {
    variables: {
      postHash,
    },
  });
  const { data: commentData, loading: commentLoading } = useQuery(
    PostByPostHashDocument,
    {
      variables: {
        postHash: replyPostHash as string,
      },
      skip: !replyPostHash,
    },
  );
  const { toast } = useToast();
  const { user } = use(UserContext);
  const submitPost = useSubmitPost();
  const { confirm } = useConfirm();

  const post = postData?.postByPostHash;
  const comment = commentData?.postByPostHash;
  const editorContextRef = useRef<EditorContextType>(null);
  const requestClose = async () => {
    if (
      editorContextRef.current &&
      !editorContextRef.current.empty() &&
      !(await confirm({
        description: `You have unsaved content. Are you sure you want to discard?`,
        type: "warning",
      }))
    ) {
      return false;
    }

    return true;
  };
  const { open: editorOpen, setOpen: setEditorOpen } = useLocalEntryDialog(
    DIALOG.CommentOverlayEditor,
    {
      beforeClose: requestClose,
    },
  );

  if (loading || commentLoading)
    return (
      <Skeleton
        className="
          mt-4 h-[100px] rounded-lg border border-border-light
          sm:ml-12
        "
      />
    );

  if (!post || post.isHidden) return null;

  const { isPureRepost, repost } = getPostData(post);

  return (
    <div
      className="
        mt-4 flex flex-col rounded-lg border border-border-light
        sm:ml-14
      "
    >
      <div className="relative rounded-2xl pb-2">
        <PostContextProvider
          post={post}
          hideMedia={isPureRepost ? !!repost?.body : !!post.body}
          hideEmbeds
          hideHeader
          hideAvatar
          hideUpvote
          hideActivity
          showMoreMaxHeight={NOTIFICATION_POST_SHOW_MORE_MAX_HEIGHT}
          postCachePrefix={`notification-post-${notificationId}`}
          forwardContextProps
        >
          <div
            className={`
              z-10 rounded-xl border-border-light p-4 pb-0
              ${
                comment
                  ? `
                    before:absolute before:top-[30%] before:left-[34px]
                    before:h-full before:w-[3px] before:bg-card
                    before:content-['']
                  `
                  : ""
              }
            `}
          >
            <NotificationPostDetails
              className="z-10"
              hideNotificationHeader={hideNotificationHeader && !isPureRepost}
            />
          </div>
        </PostContextProvider>
      </div>
      {comment && (
        <PostContextProvider
          post={comment}
          hideMedia={!!comment?.body}
          hideEmbeds
          hideHeader
          hideAvatar
          hideUpvote
          hideActivity
          showMoreMaxHeight={NOTIFICATION_POST_SHOW_MORE_MAX_HEIGHT}
          postCachePrefix={`notification-post-comment-${notificationId}`}
          forwardContextProps
        >
          <div className={`z-10 rounded-xl border-border-light p-4 pb-0`}>
            <NotificationPostDetails
              className="z-10"
              hideNotificationHeader={false}
            />
          </div>

          <Dialog
            open={editorOpen}
            onOpenChange={async (o) => {
              if (o) {
                setEditorOpen(true);
                return;
              }

              if (!(await requestClose())) {
                return false;
              }

              setEditorOpen(false);
            }}
          >
            <StopPropagationWrapper
              className="
                mt-2 mr-4 mb-4 ml-16 flex items-center gap-2 text-muted
                hover:text-primary
              "
              onClick={() => {
                setEditorOpen(true);
              }}
            >
              <LuMessagesSquare />
              <span className="text-xs">
                Reply to {getUserName({ account: comment.poster })}
              </span>
            </StopPropagationWrapper>
            <DialogContent
              className="
                content h-(--overlay-keyboard-available-height)
                max-h-(--overlay-keyboard-available-height) max-w-full
                overflow-hidden rounded-none border-0
                md:max-h-[90vh] md:max-w-[700px] md:rounded-lg
              "
              closeClassName="top-2 right-2 size-9 p-2"
              onOpenAutoFocus={(event) => {
                event.preventDefault();
              }}
            >
              <DialogTitle className="hidden">Add a Comment</DialogTitle>
              <DialogDescription className="hidden">
                Add Comment
              </DialogDescription>
              <EditorContextProvider
                ref={editorContextRef}
                autoFocus
                enableFileDrop
                onSubmit={async (editorContext) => {
                  const { setEditorContext } = editorContext;
                  let transactionSubmitted = false;

                  setEditorContext({
                    submitting: true,
                  });

                  const requestData = await toPostRequestParams({
                    editorContext,
                    user: user!,
                    initialData: {
                      UpdaterPublicKeyBase58Check: user?.publicKey,
                      ParentStakeID: comment?.postHash,
                    },
                  });
                  try {
                    const createdComment = await submitPost(requestData, {
                      onTransactionSubmitted: () => {
                        transactionSubmitted = true;
                        setEditorOpen(false);
                      },
                      toleratePostHydrationFailure: true,
                    });

                    toast({
                      title: "Comment submitted",
                      description: createdComment?.postHash ? (
                        <>
                          Comment added successfully.
                          <br />
                          <Link
                            href={`/post/${createdComment.postHash}`}
                            className="underline"
                            prefetch={false}
                          >
                            View Reply
                          </Link>
                        </>
                      ) : (
                        "Comment added successfully."
                      ),
                      variant: "success",
                    });
                  } catch (error) {
                    toast({
                      title: "Error submitting comment",
                      description: `An error happened when submitting your comment`,
                      details: createToastErrorDetails(error),
                      variant: "destructive",
                    });
                  } finally {
                    if (!transactionSubmitted) {
                      setEditorContext({
                        submitting: false,
                      });
                    }
                  }
                }}
              >
                <CommentOverlayEditor to={post.poster!.username!} />
              </EditorContextProvider>
            </DialogContent>
          </Dialog>
        </PostContextProvider>
      )}
    </div>
  );
};
